import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface AssistantConfig {
  id: string;
  greeting_message: string;
  assistant_name: string;
  assistant_description: string;
  is_active: boolean;
}

interface AssistantResponse {
  id: string;
  keywords: string[];
  response_text: string;
  action_type: string | null;
  action_value: string | null;
  button_label: string | null;
  is_active: boolean;
}

interface QuickAction {
  id: string;
  label: string;
  response_key: string;
  is_active: boolean;
  order_index: number;
}

const fetchAssistantConfig = async () => {
  const { data, error } = await supabase
    .from('virtual_assistant_config')
    .select('*')
    .eq('is_active', true)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error loading assistant config:', error);
    return null;
  }
  return data as AssistantConfig | null;
};

const fetchAssistantResponses = async () => {
  const { data, error } = await supabase
    .from('virtual_assistant_responses')
    .select('*')
    .eq('is_active', true)
    .order('created_at');

  if (error) {
    console.error('Error loading responses:', error);
    return [];
  }
  return (data || []) as AssistantResponse[];
};

const fetchQuickActions = async () => {
  const { data, error } = await supabase
    .from('virtual_assistant_quick_actions')
    .select('*')
    .eq('is_active', true)
    .order('order_index');

  if (error) {
    console.error('Error loading quick actions:', error);
    return [];
  }
  return (data || []) as QuickAction[];
};

export const useAssistantConfig = () => {
  const { data: config, isLoading: configLoading, refetch: refetchConfig } = useQuery({
    queryKey: ['assistant-config'],
    queryFn: fetchAssistantConfig,
    staleTime: 5 * 60 * 1000,
  });

  const { data: responses = [], isLoading: responsesLoading } = useQuery({
    queryKey: ['assistant-responses'],
    queryFn: fetchAssistantResponses,
    staleTime: 5 * 60 * 1000,
    enabled: !!config, // Only fetch when config exists
  });

  const { data: quickActions = [], isLoading: actionsLoading } = useQuery({
    queryKey: ['assistant-quick-actions'],
    queryFn: fetchQuickActions,
    staleTime: 5 * 60 * 1000,
    enabled: !!config, // Only fetch when config exists
  });

  const [localConfig, setLocalConfig] = useState<AssistantConfig | null>(null);

  const setConfig = (c: AssistantConfig | null) => setLocalConfig(c);

  const updateConfig = async (updates: Partial<AssistantConfig>) => {
    const currentConfig = localConfig || config;
    if (!currentConfig) return false;

    try {
      const { error } = await supabase
        .from('virtual_assistant_config')
        .update(updates)
        .eq('id', currentConfig.id);

      if (error) {
        console.error('Error updating config:', error);
        return false;
      }

      setLocalConfig({ ...currentConfig, ...updates });
      return true;
    } catch (error) {
      console.error('Error updating assistant config:', error);
      return false;
    }
  };

  const addResponse = async (response: Omit<AssistantResponse, 'id'>) => {
    try {
      const { data, error } = await supabase
        .from('virtual_assistant_responses')
        .insert(response)
        .select()
        .single();

      if (error) {
        console.error('Error adding response:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error adding response:', error);
      return false;
    }
  };

  const updateResponse = async (id: string, updates: Partial<AssistantResponse>) => {
    try {
      const { error } = await supabase
        .from('virtual_assistant_responses')
        .update(updates)
        .eq('id', id);

      if (error) {
        console.error('Error updating response:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating response:', error);
      return false;
    }
  };

  const deleteResponse = async (id: string) => {
    try {
      const { error } = await supabase
        .from('virtual_assistant_responses')
        .update({ is_active: false })
        .eq('id', id);

      if (error) {
        console.error('Error deleting response:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting response:', error);
      return false;
    }
  };

  const addQuickAction = async (action: Omit<QuickAction, 'id'>) => {
    try {
      const { data, error } = await supabase
        .from('virtual_assistant_quick_actions')
        .insert(action)
        .select()
        .single();

      if (error) {
        console.error('Error adding quick action:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error adding quick action:', error);
      return false;
    }
  };

  const updateQuickAction = async (id: string, updates: Partial<QuickAction>) => {
    try {
      const { error } = await supabase
        .from('virtual_assistant_quick_actions')
        .update(updates)
        .eq('id', id);

      if (error) {
        console.error('Error updating quick action:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating quick action:', error);
      return false;
    }
  };

  const deleteQuickAction = async (id: string) => {
    try {
      const { error } = await supabase
        .from('virtual_assistant_quick_actions')
        .update({ is_active: false })
        .eq('id', id);

      if (error) {
        console.error('Error deleting quick action:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting quick action:', error);
      return false;
    }
  };

  const loading = configLoading || responsesLoading || actionsLoading;

  return {
    config: localConfig || config || null,
    setConfig,
    responses,
    quickActions,
    loading,
    updateConfig,
    addResponse,
    updateResponse,
    deleteResponse,
    addQuickAction,
    updateQuickAction,
    deleteQuickAction,
    reloadConfig: refetchConfig
  };
};
